/**
 * useCreateTableWithLimit Hook
 *
 * Wrapper hook that enforces table limit checks before opening the create table sheet.
 * This centralizes the paywall logic to prevent bypassing limit checks.
 *
 * Usage:
 * ```tsx
 * const { openCreateTable, LimitDialog } = useCreateTableWithLimit();
 *
 * return (
 *   <>
 *     <Button onClick={() => openCreateTable()}>Create Table</Button>
 *     <LimitDialog />
 *   </>
 * );
 * ```
 */

import { useState, useCallback, type ReactNode } from 'react';

import { UpgradeDialog, type UpgradeDialogContext } from '@/components/billing';
import { useUsageEnforcement } from '@/hooks/useUsageEnforcement';
import { useCreateTableStore } from '@/features/workspace/stores/useCreateTableStore';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';

interface LimitDialogData {
  count: number;
  limit: number;
}

/**
 * Hook that wraps table creation with limit enforcement
 *
 * @returns Object containing:
 * - openCreateTable: Function to open create table sheet (with limit check)
 * - LimitDialog: Component to render the upgrade dialog
 */
export function useCreateTableWithLimit() {
  const { selectedWorkspaceId } = useWorkspaceStore();
  const { canCreateTable } = useUsageEnforcement(selectedWorkspaceId || '');
  const openSheet = useCreateTableStore((s) => s.openSheet);

  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [limitDialogData, setLimitDialogData] = useState<LimitDialogData | null>(null);
  const [dialogContext, setDialogContext] = useState<UpgradeDialogContext>('table_limit');

  /**
   * Open create table sheet with limit check.
   * Always opens a fresh form — drafts are only resumed via explicit click in the workspace explorer.
   */
  const openCreateTable = useCallback(
    (folderId?: string | null) => {
      const result = canCreateTable();

      // If still loading, just open the sheet - backend will validate
      if (result.isLoading) {
        openSheet(folderId);
        return;
      }

      if (!result.allowed) {
        // If limit is 0, it means subscription expired or no plan
        if (result.limit === 0) {
          setDialogContext('subscription_expired');
          setLimitDialogData(null);
        } else if (result.limit !== undefined && result.limit !== null) {
          setDialogContext('table_limit');
          setLimitDialogData({
            count: result.current ?? 0,
            limit: result.limit,
          });
        } else {
          // Limit is undefined/null = unlimited, shouldn't reach here
          openSheet(folderId);
          return;
        }
        setShowLimitDialog(true);
        return;
      }

      // Open fresh sheet
      openSheet(folderId);
    },
    [canCreateTable, openSheet]
  );

  /**
   * Render the limit dialog component
   * Must be included in the component tree
   */
  const LimitDialog = useCallback(
    (): ReactNode => {
      if (!showLimitDialog) return null;

      // Subscription expired or no plan - show subscription dialog
      if (dialogContext === 'subscription_expired' || !limitDialogData) {
        return (
          <UpgradeDialog
            context="subscription_expired"
            open={showLimitDialog}
            onOpenChange={setShowLimitDialog}
            workspaceId={selectedWorkspaceId || ''}
          />
        );
      }

      // Table limit reached - show table limit dialog
      return (
        <UpgradeDialog
          context="table_limit"
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          tableCount={limitDialogData.count}
          tableLimit={limitDialogData.limit}
          workspaceId={selectedWorkspaceId || ''}
        />
      );
    },
    [limitDialogData, showLimitDialog, dialogContext]
  );

  return {
    openCreateTable,
    LimitDialog,
    showLimitDialog,
    setShowLimitDialog,
    limitDialogData,
  };
}
